import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import CheckCircle from "@mui/icons-material/CheckCircle";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import messages from "../../assets/locale/messages";
import { PasswordMatchBadgeIcon } from "../../utils/Icons/PasswordMatchBadgeIcon";
import { UIPasswordField } from "../ui";
import "./PasswordPairWithRules.scss";

/**
 * Password + confirm password row (responsive two columns) and optional rule hints
 * below — same layout and styling as Contact Info registration step.
 *
 * @param {Array<{ label: string, satisfied?: boolean }>} rules - If `satisfied` is
 *   omitted, rows always use the blue info icon (registration). If boolean, shows
 *   green check when true and info when false (e.g. reset password validation).
 */
const PasswordPairWithRules = ({
  passwordId,
  confirmId,
  passwordName,
  confirmName,
  passwordLabel,
  confirmLabel,
  passwordPlaceholder,
  confirmPlaceholder,
  passwordValue,
  confirmValue,
  onPasswordChange,
  onConfirmChange,
  onBlur,
  passwordRequired,
  passwordError,
  confirmError,
  passwordTouched,
  confirmTouched,
  rules,
  showExternalConfirmMatch,
  confirmMatchVisible,
  confirmMatchAriaLabel,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { inputsValidations } = messages[lang];
  const passwordHelperText = passwordTouched
    ? inputsValidations[passwordError]
    : "";
  const confirmHelperText = confirmTouched
    ? inputsValidations[confirmError]
    : "";

  const confirmColumn = showExternalConfirmMatch ? (
    <div className="password-pair-with-rules__confirm-wrap">
      <UIPasswordField
        id={confirmId}
        name={confirmName}
        label={confirmLabel}
        placeholder={confirmPlaceholder}
        value={confirmValue}
        onValueChange={onConfirmChange}
        onBlur={onBlur}
        required={passwordRequired}
        error={Boolean(confirmTouched && confirmError)}
        helperText={confirmHelperText}
      />
      {confirmMatchVisible ? (
        <span
          className="password-pair-with-rules__external-check"
          aria-label={confirmMatchAriaLabel || ""}
          role="img"
        >
          <PasswordMatchBadgeIcon className="password-pair-with-rules__external-check-icon" />
        </span>
      ) : null}
    </div>
  ) : (
    <UIPasswordField
      id={confirmId}
      name={confirmName}
      label={confirmLabel}
      placeholder={confirmPlaceholder}
      value={confirmValue}
      onValueChange={onConfirmChange}
      onBlur={onBlur}
      required={passwordRequired}
      error={Boolean(confirmTouched && confirmError)}
      helperText={confirmHelperText}
    />
  );

  return (
    <div className="password-pair-with-rules w-100">
      <div className="row">
        <div className="col-12 col-md-6">
          <UIPasswordField
            id={passwordId}
            name={passwordName}
            label={passwordLabel}
            placeholder={passwordPlaceholder}
            value={passwordValue}
            onValueChange={onPasswordChange}
            onBlur={onBlur}
            required={passwordRequired}
            error={Boolean(passwordTouched && passwordError)}
            helperText={passwordHelperText}
          />
        </div>
        <div className="col-12 col-md-6">{confirmColumn}</div>
      </div>

      {rules && rules.length > 0 ? (
        <div className="row password-pair-with-rules__rules-row">
          <div className="col-12">
            <ul className="password-pair-with-rules__rules list-unstyled mb-0">
              {rules.map((rule, index) => {
                const hasProgress = typeof rule.satisfied === "boolean";
                const showMet = hasProgress && rule.satisfied;
                const showUnmet = hasProgress && !rule.satisfied;
                return (
                  <li
                    key={`${rule.label}-${index}`}
                    className={`password-pair-with-rules__rule ${
                      showMet ? "password-pair-with-rules__rule--met" : ""
                    }${showUnmet ? " password-pair-with-rules__rule--unmet" : ""}`}
                  >
                    {showMet ? (
                      <CheckCircle
                        className="password-pair-with-rules__rule-icon password-pair-with-rules__rule-icon--met"
                        sx={{ fontSize: 16 }}
                        aria-hidden
                      />
                    ) : (
                      <InfoOutlined
                        className={`password-pair-with-rules__rule-icon ${
                          showUnmet
                            ? "password-pair-with-rules__rule-icon--unmet"
                            : "password-pair-with-rules__rule-icon--info"
                        }`}
                        sx={{ fontSize: 16 }}
                        aria-hidden
                      />
                    )}
                    <span>{rule.label}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
};

PasswordPairWithRules.propTypes = {
  passwordId: PropTypes.string.isRequired,
  confirmId: PropTypes.string.isRequired,
  passwordName: PropTypes.string.isRequired,
  confirmName: PropTypes.string.isRequired,
  passwordLabel: PropTypes.string.isRequired,
  confirmLabel: PropTypes.string.isRequired,
  passwordPlaceholder: PropTypes.string.isRequired,
  confirmPlaceholder: PropTypes.string.isRequired,
  passwordValue: PropTypes.string.isRequired,
  confirmValue: PropTypes.string.isRequired,
  onPasswordChange: PropTypes.func.isRequired,
  onConfirmChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  passwordRequired: PropTypes.bool,
  passwordError: PropTypes.string,
  confirmError: PropTypes.string,
  passwordTouched: PropTypes.bool,
  confirmTouched: PropTypes.bool,
  rules: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      satisfied: PropTypes.bool,
    }),
  ),
  showExternalConfirmMatch: PropTypes.bool,
  confirmMatchVisible: PropTypes.bool,
  confirmMatchAriaLabel: PropTypes.string,
  labelClassName: PropTypes.string,
};

PasswordPairWithRules.defaultProps = {
  onBlur: undefined,
  passwordRequired: false,
  passwordError: undefined,
  confirmError: undefined,
  passwordTouched: false,
  confirmTouched: false,
  rules: null,
  showExternalConfirmMatch: false,
  confirmMatchVisible: false,
  confirmMatchAriaLabel: "",
  labelClassName: "font-medium mb-2",
};

export default PasswordPairWithRules;
